import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { Button } from 'react-bootstrap';
import SeamlessVideoPlayer from '../playback/SeamlessVideoPlayer';
import { playbackCatalog, playbackKeysByUrl, planNextMedia } from '../playback/catalog';
import TypeOfAnimationBlock from './typeOfAnimationBlock';
import { Reorder } from "framer-motion";
import { useHookContext } from './hookContext';
import { scenariosDict } from '../support/dictionaries';
import {translateButton} from '../support/dictionaries';
import useRemoteController from '../remote/useRemoteController';
import RemoteConnectionPanel from '../remote/RemoteConnectionPanel';
export default function MainDisplay(props) {
    const { lang, remote = false } = props;
    const playerRef = useRef(null);
    const playerWrapperRef = useRef(null);
    const videoClickTimerRef = useRef(null);
    const pendingTargetRef = useRef(null);
    const [showVideoControls, setShowVideoControls] = useState(false);

    const {
        curVideo, setCurVideo,
        scenarios, setScenarios,
        playSequence, setPlaySequence,
        declineBtn, setDeclineBtn,
        acceptBtn, setAcceptBtn,
        setCurrentlyPlaying,
        typeOfAnimation, setTypeOfAnimation,
    } = useHookContext();

    const currentKey = playbackKeysByUrl.get(curVideo);
    const queuedKey = scenarios[0];
    const preloadKey = pendingTargetRef.current ||
        (queuedKey ? planNextMedia(currentKey, queuedKey, true).key : null);
    const preloadedVideo = playbackCatalog[preloadKey] || '';
    const remoteConnection = useRemoteController(playerRef, curVideo, preloadedVideo, remote, lang);

    useEffect(() => () => {
        window.clearTimeout(videoClickTimerRef.current);
    }, []);

    const swapVideo = function(nextVideo, allowBridge = true) {
        if (!nextVideo) return;
        const video = playerRef.current?.getInternalPlayer?.();
        const targetKey = playbackKeysByUrl.get(nextVideo);
        const planned = planNextMedia(currentKey, targetKey, allowBridge && !!video?.ended);
        pendingTargetRef.current = planned.target;
        const source = playbackCatalog[planned.key];
        if (source === curVideo) {
            playerRef.current?.seekTo(0);
            video?.play()?.catch(() => {});
        } else setCurVideo(source);
        setCurrentlyPlaying(true);
    };

    const startNext = function() {
        if (!scenarios.length) return;
        resetReactions();
        swapVideo(scenariosDict[scenarios[0]]);
        setScenarios(queue => queue.slice(1));
    };

    const playNext = function() {
        setPlaySequence(false);
        startNext();
    };

    const playAll = function() {
        if (!scenarios.length) return;
        setPlaySequence(true);
        startNext();
    };

    const handleVideoEnd = function() {
        if (pendingTargetRef.current) {
            const target = pendingTargetRef.current;
            pendingTargetRef.current = null;
            swapVideo(scenariosDict[target], false);
        } else if (playSequence && scenarios.length) startNext();
        else {
            // Hold the last taught action instead of jumping to a differently framed idle.
            setPlaySequence(false);
            setCurrentlyPlaying(false);
        }
    };

    const handleVideoError = function() {
        if (pendingTargetRef.current) {
            const target = pendingTargetRef.current;
            pendingTargetRef.current = null;
            swapVideo(scenariosDict[target], false);
        } else {
            setPlaySequence(false);
            setCurrentlyPlaying(false);
        }
    };

    const handleVideoClick = function() {
        if (showVideoControls) {
            return;
        }

        window.clearTimeout(videoClickTimerRef.current);
        videoClickTimerRef.current = window.setTimeout(playAll, 220);
    }

    const toggleVideoFullscreen = async function() {
        window.clearTimeout(videoClickTimerRef.current);
        const playerWrapper = playerWrapperRef.current;

        if (!playerWrapper) {
            return;
        }

        if (document.fullscreenElement) {
            await document.exitFullscreen();
        } else {
            await playerWrapper.requestFullscreen();
        }
    }
    
    const resetReactions = function() {
        setAcceptBtn('');
        setDeclineBtn('');
    }

    const reactToAnimation = function(reaction) {
        if (reaction === 'accept') {
            setAcceptBtn('accepted');
            setDeclineBtn('');
        } else {
            setAcceptBtn('');
            setDeclineBtn('declined');
        }
    }

    const typesOfAnimationList = [
        { value: 'face', label: translateButton('face', lang) },
        { value: 'body', label: translateButton('body', lang) },
        { value: 'count', label: translateButton('count', lang) },
        { value: 'room', label: translateButton('room', lang) },
        { value: 'bedroom', label: translateButton('bedroom', lang) },
        { value: 'bedroom_waking_up', label: translateButton('bedroom_waking_up', lang) },
        { value: 'kitchen1', label: translateButton('kitchen1', lang) },
        { value: 'kitchen2', label: translateButton('kitchen2', lang) },
        { value: 'kitchen3', label: translateButton('kitchen3', lang) },
        { value: 'garden', label: translateButton('garden', lang) }
    ];

    return (
        <>
        {remote && <RemoteConnectionPanel connection={remoteConnection} lang={lang} />}
        <main className='mainBox'>
            <section className='playerColumn' aria-label='Video player'>
                <div className='videoBox'>
                    <div
                        ref={playerWrapperRef}
                        className={`player-wrapper${showVideoControls ? '' : ' playerInteractive'}`}
                        onClick={handleVideoClick}
                        onDoubleClick={toggleVideoFullscreen}
                    >
                    <SeamlessVideoPlayer
                        ref={playerRef}
                        className='react-player'
                        url={curVideo}
                        nextUrl={preloadedVideo}
                        controls={showVideoControls}
                        onEnded={handleVideoEnd}
                        onError={handleVideoError}
                        onPlaying={remoteConnection.publishPlayback}
                    />
                    <button
                        className='videoControlsToggle'
                        type='button'
                        aria-label='Video controls'
                        aria-pressed={showVideoControls}
                        title='Video controls'
                        onClick={(event) => {
                            event.stopPropagation();
                            setShowVideoControls(controlsVisible => !controlsVisible);
                        }}
                        onDoubleClick={event => event.stopPropagation()}
                    >
                        &#8942;
                    </button>
                    </div>

                </div>

                <div className='buttonGridMarks' role='group'>
                    <Button 
                        className={acceptBtn}
                        type='button' 
                        variant='success'
                        value='accept' 
                        size='md'
                        onClick={(value) => {
                            setPlaySequence(false);
                            reactToAnimation(value.target.value);
                            swapVideo(scenariosDict.thumbsUp);
                        }}
                    >
                    {translateButton("Thumbs up",lang)}
                    </Button>

                    <Button 
                        className={declineBtn}
                        type='button' 
                        variant='danger'
                        value='decline' 
                        size='md'
                        onClick={(value) => {
                            setPlaySequence(false);
                            reactToAnimation(value.target.value);
                            swapVideo(scenariosDict.no);
                        }}               
                        >
                    {translateButton("No",lang)}
                    </Button>
                </div>
            </section>
            <section className='scenarioColumn'>
                <div className='scenarioControls'>
                     <div className="buttonGridOptions primaryActions">
                <Button 
                    type='button' 
                    variant='primary'
                    value='Play next' 
                    size='md'
                    onClick={() => playNext()}
                >
                {translateButton("Play next",lang)}
                </Button>

                <Button 
                    type='button' 
                    variant='primary'
                    value='Play sequence' 
                    size='md'
                    onClick={() => playAll()}
                >
                {translateButton("Play sequence",lang)}
                </Button>
            </div>

                    <label>{translateButton("Select the type of scenarios:",lang)} </label>
                    <Select 
                        options = {typesOfAnimationList} 
                        className='setsSelector'
                        placeholder='Select...'
                        value={typesOfAnimationList.find(option => option.value === typeOfAnimation.value)}
                        onChange={value => {setTypeOfAnimation(value)}}
                    />
                    <TypeOfAnimationBlock lang={lang} />
                </div>
            </section>
            <aside className='queueColumn'>
                <label>{translateButton("Queue:",lang)}</label>
                    <Reorder.Group className='queueList' as='div' values={scenarios} onReorder={setScenarios}>
                        {scenarios.map((elem, idx) => (
                            <Reorder.Item className='queueItem' as='div' key={`${elem}-${idx}`} value={elem} drag='y'>
                                <Button
                                    className={`orderedItem`}
                                    type='none' 
                                    variant='info'
                                    value={elem + idx}
                                    size='md'
                                    onDoubleClick={() => {
                                        const result = [];
                                        for (const [index, scenario] of scenarios.entries()) {
                                            if (index !== idx) {
                                                result.push(scenario);
                                            }
                                        }
                                        console.log(elem);
                                        setCurrentlyPlaying(true);
                                        setScenarios(result);
                                    }}
                                    >
                                {
                                translateButton(elem,lang)
                                ///\d/.test(elem) ? elem.match(/\d/g).join('') : textToSigns(elem)
                                }
                                </Button>
                            </Reorder.Item>
                        ))}
                    </Reorder.Group>  
            </aside>
        </main>
        </>
    )
}
